import * as React from 'react'
import { useWindowSize } from 'usehooks-ts'

import { DASHBOARD_MOBILE_LAYOUT_BREAKPOINT } from '@/features/dashboard/lib/grid-layout-config'

const MOBILE_BREAKPOINT = 768
const LAPTOP_BREAKPOINT = 1440
const DESKTOP_BREAKPOINT = 1920
const MOBILE_USER_AGENT_PATTERN =
  /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini|Mobi/i

enum ScreenBreakPoint {
  MOBILE,
  LAPTOP,
  DESKTOP,
  STUDIO,
}

const detectMobileDevice = () => {
  if (typeof navigator === 'undefined') return false

  const navigatorWithUserAgentData = navigator as Navigator & {
    userAgentData?: { mobile?: boolean }
  }
  if (navigatorWithUserAgentData.userAgentData?.mobile === true) return true

  const userAgent = navigator.userAgent
  const isIPadOS = /Macintosh/i.test(userAgent) && navigator.maxTouchPoints > 1
  return MOBILE_USER_AGENT_PATTERN.test(userAgent) || isIPadOS
}

const subscribeToMobileDevice = () => () => {}

export function useIsMobileDevice() {
  return React.useSyncExternalStore(subscribeToMobileDevice, detectMobileDevice, () => false)
}

export function useMediaQuery(query: string) {
  const subscribe = React.useCallback(
    (onStoreChange: () => void) => {
      const mediaQuery = window.matchMedia(query)
      mediaQuery.addEventListener('change', onStoreChange)
      return () => mediaQuery.removeEventListener('change', onStoreChange)
    },
    [query]
  )
  const getSnapshot = React.useCallback(() => window.matchMedia(query).matches, [query])

  return React.useSyncExternalStore(subscribe, getSnapshot, () => false)
}

/**
 * For a picker's search field: focus it on open with a mouse, not on a touch screen — there the
 * keyboard would jump up over half the list before the user decided to search rather than
 * scroll. A field the user tapped to edit (an inline price editor) should keep plain autoFocus.
 */
export function useCanAutoFocusSearch() {
  return !useMediaQuery('(pointer: coarse)')
}

/**
 * The app's one "mobile layout" test: a real mobile device (user agent) at or below
 * DASHBOARD_MOBILE_LAYOUT_BREAKPOINT. The header (hamburger instead of the side panel), the
 * side panel, the stacked mobile dashboard and the full-page routes all switch on this — they
 * used to each pick their own rule (768px / 1024px / UA only), so a phone in landscape or an
 * iPad in portrait got the stacked dashboard with the desktop header and no side panel, i.e. no
 * navigation at all. A narrow desktop window is never "mobile": it keeps the desktop chrome.
 */
export function useIsMobileLayout() {
  const isMobileDevice = useIsMobileDevice()
  const isWithinBreakpoint = useMediaQuery(`(max-width: ${DASHBOARD_MOBILE_LAYOUT_BREAKPOINT}px)`)
  return isMobileDevice && isWithinBreakpoint
}

export function useScreenBreakPoint() {
  // Layout viewport (window size), not visualViewport: pinch / ctrl-scroll zoom shrinks the
  // visual viewport, and breakpoints read from it flipped the app into its laptop or mobile
  // layout mid-zoom.
  const { width = 0, height = 0 } = useWindowSize()
  const isMobileDevice = useIsMobileDevice()

  const screen = React.useMemo(() => ({ w: width, h: height }), [height, width])

  const screenBreakPoint = React.useMemo(() => {
    if (screen.w <= MOBILE_BREAKPOINT) return ScreenBreakPoint.MOBILE
    if (screen.w <= LAPTOP_BREAKPOINT) return ScreenBreakPoint.LAPTOP
    if (screen.w <= DESKTOP_BREAKPOINT) return ScreenBreakPoint.DESKTOP
    return ScreenBreakPoint.STUDIO
  }, [screen.w])

  return {
    isMobile: screenBreakPoint <= ScreenBreakPoint.MOBILE,
    isLaptop: screenBreakPoint <= ScreenBreakPoint.LAPTOP,
    isDesktop: screenBreakPoint <= ScreenBreakPoint.DESKTOP,
    isStudio: screenBreakPoint <= ScreenBreakPoint.STUDIO,
    isMobileDevice,
    screen,
  }
}
